import {
  AutoComplete,
  FormContainer,
  FormInput,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Grid, Tooltip } from "@mui/material";
import { memo, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import MainApi from "../../../../../../../services/mainApi.service";
import { listMainGenerics } from "../../../../../../../services/generic.service";
import {
  createIntegrationRuleNotification,
  getIntegrationRuleNotificationById,
  updateIntegrationRuleNotificationById,
} from "../../../../../../../services/integrationRuleNotification.service";
import {
  integrationRuleNotificationSchema,
  TIntegrationRuleNotificationForm,
} from "./integrationRuleNotificationSchema";
import { IIntegrationRuleNotificationProps } from "./models";

const PROFILE_ROUTE = process.env.REACT_APP_MAIN + "/api/v1/Profile/";
const profileService = new MainApi(PROFILE_ROUTE);
const translationPath = "page.4uBus.integrationRule.";

export const IntegrationRuleNotification = memo(
  ({
    id,
    busIntegrationRuleId,
    onBackButton,
  }: IIntegrationRuleNotificationProps) => {
    const { t } = useTranslation();

    const { sendRequest: save, loading: sLoading } = useFetch(
      createIntegrationRuleNotification,
    );

    const { sendRequest: update, loading: uLoading } = useFetch(
      updateIntegrationRuleNotificationById,
    );

    const { sendRequest: get, loading: gLoading } = useFetch(
      getIntegrationRuleNotificationById,
    );

    const saveHandler = (v: TIntegrationRuleNotificationForm) =>
      save({
        ...v,
        _4uBusIntegrationRuleId: busIntegrationRuleId ?? "",
        profileId: v.profile.id,
        typelNotificationId: v.typelNotification.id,
        channelNotificationId: v.channelNotification.id,
      });

    const updateHandler = (v: TIntegrationRuleNotificationForm, id: string) =>
      update({
        item: {
          ...v,
          _4uBusIntegrationRuleId: busIntegrationRuleId ?? "",
          profileId: v.profile.id,
          typelNotificationId: v.typelNotification.id,
          channelNotificationId: v.channelNotification.id,
        },
        id,
      });

    const getHandler = useCallback(
      async (
        id: string,
      ): Promise<Treturn<TIntegrationRuleNotificationForm>> => {
        const { data, success } = await get(id);
        if (data && success) {
          const newData: TIntegrationRuleNotificationForm = {
            ...data,
          };
          return { data: newData, success: true };
        }

        return { data, success };
      },
      [get],
    );

    const channelNotificationParams = useMemo(
      () => ({
        Identifier: "ChannelNotification",
      }),
      [],
    );

    const typelNotificationParams = useMemo(
      () => ({
        Identifier: "TypelNotification",
      }),
      [],
    );

    const loading = sLoading || uLoading || gLoading;

    return (
      <FormContainer<TIntegrationRuleNotificationForm>
        saveFunction={saveHandler}
        updateFunction={updateHandler}
        getFunction={getHandler}
        loading={loading}
        title="Notificação"
        schema={integrationRuleNotificationSchema}
        subtitle="Nova notificação"
        backButtonOnClick={onBackButton}
        redirectOnSave={false}
        onSuccess={onBackButton}
        isModalForm={true}
        propId={id}
        formActionsOnBottom={false}
        fields={() => (
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <AutoComplete
                request={profileService.list}
                name="profile"
                size="small"
                getOptionLabel={({ name }) => name}
                label="Perfil"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <AutoComplete
                request={listMainGenerics}
                params={channelNotificationParams}
                name="channelNotification"
                size="small"
                getOptionLabel={({ name }) => name}
                label="Canal"
              />
            </Grid>

            <Grid item xs={12} sm={6}>
              <AutoComplete
                request={listMainGenerics}
                params={typelNotificationParams}
                name="typelNotification"
                size="small"
                getOptionLabel={({ name }) => name}
                label="Tipo de notificação"
              />
            </Grid>

            <Grid item xs={12}>
              <FormInput
                size="small"
                name="content"
                fullWidth
                multiline
                minRows={4}
                InputProps={{
                  endAdornment: (
                    <Tooltip
                      title={t(translationPath + "reports_tooltip", {
                        NR_LAUDOS: "{{NR_LAUDOS}}",
                      })}
                    >
                      <InfoOutlinedIcon color="primary" fontSize="small" />
                    </Tooltip>
                  ),
                }}
                label="Conteúdo"
              />
            </Grid>

            <Grid item xs={12}>
              <FormInput
                size="small"
                name="emailCopyRecipients"
                fullWidth
                multiline
                minRows={2}
                InputProps={{
                  endAdornment: (
                    <Tooltip
                      title={t(
                        translationPath + "email_copy_recipients_tooltip",
                      )}
                    >
                      <InfoOutlinedIcon color="primary" fontSize="small" />
                    </Tooltip>
                  ),
                }}
                label="Destinatários em cópia"
              />
            </Grid>
          </Grid>
        )}
      />
    );
  },
);
